import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useProject, useUpdateProject } from "@/hooks/use-projects";
import { ProjectHeader } from "@/components/projects/project-header";
import { RegenerateAllDialog } from "@/components/projects/regenerate-all-dialog";
import { ScheduleAllDialog } from "@/components/projects/schedule-all-dialog";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { Calendar, RotateCcw } from "lucide-react";

const NON_APPROVED_STATUSES = new Set(["DRAFT", "PENDING_REVIEW", "REJECTED"]);

export function BrandProjectDetailPage() {
  const { t } = useTranslation("projects");
  const { projectId } = useParams<{ projectId: string }>();
  const { brand } = useBrandContext();
  const { canEdit, canSchedule } = usePermissions();
  const { data: project, isLoading } = useProject(brand.id, projectId!);
  const updateProject = useUpdateProject(brand.id, projectId!);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [scheduleAllOpen, setScheduleAllOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!project) {
    return <div className="text-muted-foreground">{t("projects:page.notFound")}</div>;
  }

  const pieces = project.pieces ?? [];
  const nonApprovedCount = pieces.filter((p) => NON_APPROVED_STATUSES.has(p.status)).length;
  const approvedCount = pieces.filter((p) => p.status === "APPROVED").length;

  return (
    <div className="space-y-6">
      <ProjectHeader
        project={project}
        pieces={pieces}
        canEditBrief={canEdit}
        onSaveBrief={(brief) => updateProject.mutate({ brief })}
        briefSaving={updateProject.isPending}
      />

      <Separator />

      <div>
        <div className="mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            {t("projects:page.piecesHeading", { count: project.pieces?.length ?? 0 })}
          </h2>
          <div className="flex gap-2">
            {canEdit && nonApprovedCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => setRegenerateOpen(true)}>
                <RotateCcw size={14} className="mr-1.5" /> {t("projects:page.regenerateAll")}
              </Button>
            )}
            {canSchedule && approvedCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => setScheduleAllOpen(true)}>
                <Calendar size={14} className="mr-1.5" /> {t("projects:page.scheduleAllApproved")}
              </Button>
            )}
          </div>
        </div>
        {project.pieces?.length ? (
          <div className="space-y-2">
            {project.pieces.map((piece) => (
              <Link
                key={piece.id}
                to={`/${brand.slug}/pieces/${piece.shortCode}`}
                className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50"
              >
                <span className="font-mono text-sm text-muted-foreground">
                  {piece.shortCode}
                </span>
                <span className="text-sm">
                  {piece.postType && POST_TYPE_META[piece.postType as PostType]
                    ? `${POST_TYPE_META[piece.postType as PostType].platform} · ${POST_TYPE_META[piece.postType as PostType].label}`
                    : `${piece.platform} · ${piece.format}`}
                </span>
                <span className="flex-1 truncate text-sm text-muted-foreground">
                  {piece.copy}
                </span>
                <StatusBadge status={piece.status} />
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("projects:page.noPieces")}</p>
        )}
      </div>

      <RegenerateAllDialog
        brandId={brand.id}
        projectId={project.id}
        discardCount={nonApprovedCount}
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
      />
      <ScheduleAllDialog
        brandId={brand.id}
        projectId={project.id}
        open={scheduleAllOpen}
        onOpenChange={setScheduleAllOpen}
      />
    </div>
  );
}
